/**
 * Shape property update utilities
 * Extracted from MyCanvas.js to reduce file size
 */

import { isShapeType } from "../../../util/shapeTypeMapper";
import { calculateLinePoints } from "./lineUtils";

/**
 * Reassign IDs for imported elements
 * @param {Array} arr - Array of elements to reassign IDs for
 * @param {Function} generateMarkerId - Function to generate marker IDs
 * @returns {Array} Array with new IDs assigned
 */
export const reassignIds = (arr, generateMarkerId) =>
  (arr || []).map((item) => ({ ...item, id: generateMarkerId() }));

/**
 * Reassign IDs for imported text elements, including partsByType structure
 * @param {Array} texts - Array of text elements to reassign IDs for
 * @param {Function} generateMarkerId - Function to generate marker IDs
 * @param {Function} uuidv4 - UUID v4 generator function
 * @returns {Array} Array of texts with new IDs assigned
 */
export const reassignTextIds = (texts = [], generateMarkerId, uuidv4) => {
  return texts.map((text) => {
    const newTextId = generateMarkerId();
    const newPartsByType = {};
    if (text.partsByType) {
      for (const [key, value] of Object.entries(text.partsByType)) {
        newPartsByType[key] = {
          ...value,
          parts: (value.parts || []).map((part) => ({
            ...part,
            id: part.id || uuidv4(), // Assign id if not already
          })),
        };
      }
    }

    return {
      ...text,
      id: newTextId,
      partsByType: newPartsByType,
    };
  });
};

/**
 * Create a function to update selected shape properties
 * @param {Object} params - Parameters for updating shape
 * @param {Object} params.selectedObject - Currently selected object
 * @param {boolean} params.isElementBeingEdited - Whether element is being edited
 * @param {Function} params.setPendingChanges - Setter for pending changes
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.setLines - Setter for lines array
 * @param {Function} params.setRectangles - Setter for rectangles array
 * @param {Function} params.setCircles - Setter for circles array
 * @param {Function} params.setImages - Setter for images array
 * @param {Function} params.setOffCanvasRectangles - Setter for off-canvas rectangles
 * @param {Function} params.setOffCanvasCircles - Setter for off-canvas circles
 * @param {Function} params.setOffCanvasImages - Setter for off-canvas images
 * @param {React.RefObject} params.stageRef - Reference to Konva stage
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to update selected shape
 */
export const createUpdateSelectedShape = ({
  selectedObject,
  getSelectedObject = null, // Optional: function to get current selectedObject (to avoid stale closures)
  isElementBeingEditedRef, // Use ref instead of value to avoid stale closures
  setPendingChanges,
  pendingChangesRef, // Ref to update synchronously
  setSelectedObject,
  setLines,
  setRectangles,
  setCircles,
  setImages,
  setOffCanvasRectangles,
  setOffCanvasCircles,
  setOffCanvasImages,
  stageRef,
  offCanvasStageRef = null, // Optional: ref to off-canvas stage (for circles/shapes drawn there)
  saveToHistory,
  isTextEditingModeRef, // Persistent ref passed from MyCanvas.js
}) => {
  return (newProperties) => {
    // CRITICAL: Get current selectedObject dynamically to avoid stale closures
    // Use getter function if provided, otherwise fall back to closure value
    // Declare once at the top so it's available throughout the function
    let currentSelectedObject = getSelectedObject ? getSelectedObject() : selectedObject;

    // DEBUG: Log to understand why saves aren't being deferred
    if (!currentSelectedObject) {
      return;
    }

    const { id, type } = currentSelectedObject;

    // DEBUG: Log when updating selected object

    const isFillUpdate =
      "fill" in newProperties || "fillColor" in newProperties;

    // Handle changingText flag for text editor state
    // When opening text editor (changingText: true), reset pendingChanges to false and track mode
    // When closing text editor (changingText: false), allow pendingChanges to be set if there are actual changes
    // This prevents history saves when just opening the text editor without making changes
    if ("changingText" in newProperties) {
      if (newProperties.changingText) {
        // Opening text editor - reset pendingChanges and track that we're in text editing mode
        isTextEditingModeRef.current = true;
        // Explicitly reset pendingChanges multiple times to ensure it's false
        setPendingChanges(false);
        // Use setTimeout to ensure state update completes
        setTimeout(() => {
          setPendingChanges(false);
        }, 0);
      } else {
        // Closing text editor - exit text editing mode
        isTextEditingModeRef.current = false;
        // Don't set pendingChanges here - let actual changes set it
      }
    } else {
      // Check current values from refs to avoid stale closures
      const isEditing = isElementBeingEditedRef?.current ?? false;
      const isTextEditing = isTextEditingModeRef?.current ?? false;
      // CRITICAL: Update currentSelectedObject to get latest value (already declared at function start)
      // Try getter first, then fall back to closure value
      currentSelectedObject = getSelectedObject ? getSelectedObject() : selectedObject;

      // DEBUG: Log if we can't get selectedObject
      if (!currentSelectedObject && getSelectedObject) {
        const getterResult = getSelectedObject();
      }

      const hasSelectedObject = !!currentSelectedObject; // Check if element is currently selected
      // CRITICAL: Also verify that the selected object matches the object being updated
      // This ensures we only defer saves for the actually selected object, not stale closures
      const isUpdatingSelectedObject = hasSelectedObject && currentSelectedObject.id === id;

      // DEBUG: Log if isUpdatingSelectedObject is false but we expected it to be true
      if (hasSelectedObject && !isUpdatingSelectedObject) {
      }

      // CRITICAL: If element is selected (being edited), mark as pending change to group all property updates
      // This ensures color changes, text changes, dimension changes, align, verticalAlign, etc. are all grouped together
      // CRITICAL: Also set pendingChanges if align/verticalAlign properties are being changed, even in text editing mode
      // This ensures text alignment changes are saved when element_edit_complete is called
      // CRITICAL: Also set pendingChanges if text property is being changed (includes font size changes via HTML formatting)
      // This ensures font size changes and other text formatting changes are properly tracked and saved
      const isAlignPropertyChange = "align" in newProperties || "verticalAlign" in newProperties;
      const isTextPropertyChange = "text" in newProperties;

      if ((isEditing || isUpdatingSelectedObject) && (!isTextEditing || isAlignPropertyChange || isTextPropertyChange)) {
        // Normal property update during editing/selection - mark as pending change
        // Also mark if it's an align property change or text property change (even in text editing mode)
        setPendingChanges(true);
        // CRITICAL: Also update the ref to ensure endElementEditing sees the change
        if (pendingChangesRef) {
          pendingChangesRef.current = true;
        }
        if (isAlignPropertyChange) {
        }
        if (isTextPropertyChange) {
        }
      }
      // If we're in text editing mode and it's not an align/text property change, don't set pendingChanges
    }

    // Update selected object state (only spread defined values so we never overwrite e.g. x with undefined when only y is sent)
    const definedNewProps = {};
    Object.keys(newProperties).forEach((k) => {
      if (newProperties[k] !== undefined) definedNewProps[k] = newProperties[k];
    });
    setSelectedObject((prev) => {
      if (!prev || String(prev.id) !== String(id)) return prev;
      return {
        ...prev,
        ...definedNewProps,
        ...(isFillUpdate ? { isFirstTime: false } : {}),
      };
    });

    const newProps = { ...newProperties };

    if (isShapeType(type, "Line")) {
      setLines((prevLines) =>
        prevLines.map((line) => {
          if (line.id !== id) return line;

          const updatedLine = { ...line, ...newProps };

          const x = "x" in newProps ? newProps.x : line.x;
          const y = "y" in newProps ? newProps.y : line.y;
          const length =
            "length" in newProps ? newProps.length : line.length || 100;
          const angle = "angle" in newProps ? newProps.angle : line.angle || 0;

          if (
            "length" in newProps ||
            "angle" in newProps ||
            "x" in newProps ||
            "y" in newProps
          ) {
            updatedLine.points = calculateLinePoints(x, y, length, angle);
          }

          return updatedLine;
        })
      );
    }

    if (isShapeType(type, "Rect")) {
      let updatedProps = { ...newProps };
      setRectangles((prev) => {
        const result = prev.map((r) => {
          if (r.id !== id) return r;
          const updated = {
            ...r,
            ...updatedProps,
            ...(isFillUpdate &&
              r.isFirstTime !== false &&
              updatedProps.isFirstTime !== false
              ? { stroke: "#fff", strokeWidth: 0 }
              : {}),
            ...(isFillUpdate ? { isFirstTime: false } : {}),
          };
          return updated;
        });
        return result;
      });
      setOffCanvasRectangles((prev) => {
        const result = prev.map((r) => {
          if (r.id !== id) return r;
          const updated = {
            ...r,
            ...updatedProps,
            ...(isFillUpdate &&
              r.isFirstTime !== false &&
              updatedProps.isFirstTime !== false
              ? { stroke: "#fff", strokeWidth: 0 }
              : {}),
            ...(isFillUpdate ? { isFirstTime: false } : {}),
          };
          return updated;
        });
        return result;
      });
    }

    if (isShapeType(type, "Circle")) {
      setCircles((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;

          // Don't remove stroke if isFirstTime is explicitly set to false in newProps
          const shouldRemoveStroke =
            isFillUpdate &&
            c.isFirstTime !== false &&
            newProps.isFirstTime !== false;

          return {
            ...c,
            ...newProps,
            ...(shouldRemoveStroke ? { stroke: "#fff", strokeWidth: 0 } : {}),
            ...(isFillUpdate ? { isFirstTime: false } : {}),
          };
        })
      );

      // Mirror updates to off-canvas circles
      setOffCanvasCircles((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          const shouldRemoveStroke =
            isFillUpdate &&
            c.isFirstTime !== false &&
            newProps.isFirstTime !== false;
          return {
            ...c,
            ...newProps,
            ...(shouldRemoveStroke ? { stroke: "#fff", strokeWidth: 0 } : {}),
            ...(isFillUpdate ? { isFirstTime: false } : {}),
          };
        })
      );
    }

    if (isShapeType(type, "Image")) {
      setImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
              ...img,
              ...newProps,
              ...(isFillUpdate ? { isFirstTime: false } : {}),
            }
            : img
        )
      );

      // Mirror updates to off-canvas images
      setOffCanvasImages((prev) =>
        prev.map((img) =>
          img.id === id
            ? {
              ...img,
              ...newProps,
              ...(isFillUpdate ? { isFirstTime: false } : {}),
            }
            : img
        )
      );
    }

    // Update the Konva shape directly (check main stage first, then off-canvas stage)
    let shape = stageRef.current?.findOne(`#${id}`);
    let stageToDraw = stageRef.current;
    if (!shape && offCanvasStageRef?.current) {
      shape = offCanvasStageRef.current.findOne(`#${id}`);
      stageToDraw = offCanvasStageRef.current;
    }
    if (shape && stageToDraw) {
      const currentAttrs = shape.getAttrs();
      const shouldRemoveStroke =
        isFillUpdate &&
        currentAttrs.isFirstTime !== false &&
        newProps.isFirstTime !== false &&
        (!currentAttrs.stroke || currentAttrs.stroke === "#000");

      shape.setAttrs({
        ...newProps,
        ...(isFillUpdate ? { isFirstTime: false } : {}),
        ...(shouldRemoveStroke
          ? { stroke: "transparent", strokeWidth: 0 }
          : {}),
      });

      stageToDraw.batchDraw();
    }

    // CRITICAL: Only save to history if element is NOT being edited
    // When element is being edited, pendingChanges is already set above, and element_edit_complete will save
    // This groups all property changes into a single history entry when element is unselected
    // Also check if selectedObject exists - if an element is selected, defer saves to group changes
    const isEditing = isElementBeingEditedRef?.current ?? false;
    const isTextEditing = isTextEditingModeRef?.current ?? false;
    // CRITICAL: Update currentSelectedObject to get latest value (already declared at function start)
    currentSelectedObject = getSelectedObject ? getSelectedObject() : selectedObject;
    const hasSelectedObject = !!currentSelectedObject; // Check if element is currently selected
    // CRITICAL: Also verify that the selected object matches the object being updated
    // This ensures we only defer saves for the actually selected object, not stale closures
    const isUpdatingSelectedObject = hasSelectedObject && currentSelectedObject.id === id;

    // CRITICAL: If element is selected (isUpdatingSelectedObject), treat it as being edited to group changes
    // This ensures all property changes (colors, text, dimensions, etc.) are grouped until unselection
    // Only defer if we're actually updating the selected object (not a different object)
    const shouldDeferSave = isEditing || isUpdatingSelectedObject;

    // DEBUG: Log why we're saving or deferring
    if (isUpdatingSelectedObject && !shouldDeferSave) {
    }

    // Only save immediately if:
    // 1. Element is NOT being edited AND NOT selected, OR
    // 2. It's a forceHistory operation, OR
    // 3. It's closing the text editor (changingText: false)
    if (!shouldDeferSave || ("forceHistory" in newProperties && newProperties.forceHistory)) {
      // Determine the correct operationType based on element type
      let operationType = "general";
      if (isShapeType(type, "Rect")) {
        operationType = "update_rectangle_properties";
      } else if (isShapeType(type, "Circle")) {
        operationType = "update_circle_properties";
      } else if (isShapeType(type, "Image")) {
        operationType = "update_image_properties";
      }

      setTimeout(() => {
        saveToHistory(operationType);
      }, 0);
    } else if ("changingText" in newProperties && !newProperties.changingText && !isTextEditing) {
      // Closing text editor - save immediately ONLY if element is not being edited/selected
      // If element is selected, defer to group with other changes
      if (!shouldDeferSave) {
        let operationType = "general";
        if (isShapeType(type, "Rect")) {
          operationType = "update_rectangle_properties";
        } else if (isShapeType(type, "Circle")) {
          operationType = "update_circle_properties";
        }

        setTimeout(() => {
          saveToHistory(operationType);
        }, 0);
      } else {
      }
    } else {
      // Element is being edited/selected - defer save to group all changes
    }
    // If element is being edited and it's not forceHistory or closing text editor,
    // don't save - pendingChanges is already set, and element_edit_complete will save
  };
};

/**
 * Create a function to update line properties
 * @param {Object} params - Parameters for updating line properties
 * @param {boolean} params.isElementBeingEdited - Whether element is being edited
 * @param {Function} params.setPendingChanges - Setter for pending changes flag
 * @param {Function} params.setSelectedLine - Setter for selected line
 * @param {Function} params.setLines - Setter for lines array
 * @param {Function} params.setOffCanvasLines - Setter for off-canvas lines array
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to update line properties
 */
export const createUpdateLineProperties = ({
  isElementBeingEditedRef, // Use ref instead of value to avoid stale closures
  setPendingChanges,
  pendingChangesRef, // Ref to update synchronously
  setSelectedLine,
  setLines,
  setOffCanvasLines,
  setSelectedObject,
  saveToHistory,
}) => {
  return (id, newProps) => {
    const isPresetApplication = newProps.type !== undefined;


    // Check current value from ref to avoid stale closures
    const isEditing = isElementBeingEditedRef?.current ?? false;

    // Mark that there are pending changes during element editing
    if (isEditing) {
      setPendingChanges(true);
      // CRITICAL: Also update the ref to ensure endElementEditing sees the change
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }
    }

    setSelectedLine((prev) => {
      if (!prev) return null;
      return { ...prev, ...newProps, id, isFirstTime: false };
    });

    // CRITICAL: Check both lines and offCanvasLines arrays
    // Unified elements can be in either array depending on their position
    let foundInMain = false;
    let foundInOffCanvas = false;

    // First, check which array contains the line
    setLines((prevLines) => {

      const lineFound = prevLines.find((l) => l.id === id);
      if (!lineFound) {
        // Line not in main array, check off-canvas array below
        return prevLines;
      }

      foundInMain = true;

      return prevLines.map((line) => {
        if (line.id !== id) return line;


        // CRITICAL: Merge newProps into line, ensuring boolean flags are explicitly set
        // This is important because false values need to be preserved (not just truthy values)
        let updatedLine = {
          ...line,
          ...newProps,
          isFirstTime: false,
          // CRITICAL: Explicitly set boolean flags if they're in newProps
          // This ensures false values override existing true values
          ...(newProps.hasStartArrow !== undefined && { hasStartArrow: Boolean(newProps.hasStartArrow) }),
          ...(newProps.hasEndArrow !== undefined && { hasEndArrow: Boolean(newProps.hasEndArrow) }),
        };

        // For preset applications, preserve exact position and geometry
        if (isPresetApplication) {
          // Don't recalculate anything, just apply the preset as-is
          return updatedLine;
        }

        // If skipOffset is true, preserve position and geometry
        if (newProps.skipOffset) {
          // Preserve current position unless explicitly provided
          if (newProps.x !== undefined) {
            updatedLine.x = newProps.x;
          }
          if (newProps.y !== undefined) {
            updatedLine.y = newProps.y;
          }
          // Update points if provided
          if ("points" in newProps) {
            updatedLine.points = newProps.points;
          }
          // Update width/height if provided
          if (newProps.width !== undefined) {
            updatedLine.width = newProps.width;
          }
          if (newProps.height !== undefined) {
            updatedLine.height = newProps.height;
          }
          // Check if we should force the position (for preset application)
          if (newProps.forcePosition) {
            // Keep the forced position and don't recalculate
            updatedLine.x = newProps.x;
            updatedLine.y = newProps.y;
            updatedLine.width = newProps.width || updatedLine.width;
            updatedLine.height = newProps.height || updatedLine.height;
          }
        } else if ("points" in newProps && !newProps.skipOffset) {
          // Recalculate bounding box from new points
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < newProps.points.length; i += 2) {
            minX = Math.min(minX, newProps.points[i]);
            maxX = Math.max(maxX, newProps.points[i]);
            minY = Math.min(minY, newProps.points[i + 1]);
            maxY = Math.max(maxY, newProps.points[i + 1]);
          }
          updatedLine.width = maxX - minX;
          updatedLine.height = maxY - minY;
          updatedLine.x = minX;
          updatedLine.y = minY;
          // Make points relative to new position
          updatedLine.points = newProps.points.map((val, idx) =>
            idx % 2 === 0 ? val - minX : val - minY
          );
        } else {
          // If neither skipOffset nor points are provided, just merge props (preserves position)
        }


        return updatedLine;
      });
    });

    // Mirror updates to off-canvas lines
    setOffCanvasLines((prevLines) => {

      const lineFound = prevLines.find((l) => l.id === id);
      if (!lineFound) {
        // If not found in main array and not found here, log warning
        return prevLines;
      }

      foundInOffCanvas = true;

      return prevLines.map((line) => {
        if (line.id !== id) return line;


        // CRITICAL: Merge newProps into line, ensuring boolean flags are explicitly set
        // This is important because false values need to be preserved (not just truthy values)
        let updatedLine = {
          ...line,
          ...newProps,
          isFirstTime: false,
          // CRITICAL: Explicitly set boolean flags if they're in newProps
          // This ensures false values override existing true values
          ...(newProps.hasStartArrow !== undefined && { hasStartArrow: Boolean(newProps.hasStartArrow) }),
          ...(newProps.hasEndArrow !== undefined && { hasEndArrow: Boolean(newProps.hasEndArrow) }),
        };

        const isPresetApplication = newProps.type !== undefined;
        if (isPresetApplication) {
          return updatedLine;
        }

        if ("points" in newProps && newProps.skipOffset) {
          updatedLine.points = newProps.points;
          if (newProps.forcePosition) {
            updatedLine.x = newProps.x;
            updatedLine.y = newProps.y;
            updatedLine.width = newProps.width || updatedLine.width;
            updatedLine.height = newProps.height || updatedLine.height;
          } else {
            // For skipOffset, preserve the current position and only update width/height if provided
            // Don't recalculate bounding box - this preserves the line's current position
            if (newProps.width !== undefined) {
              updatedLine.width = newProps.width;
            }
            if (newProps.height !== undefined) {
              updatedLine.height = newProps.height;
            }
          }
        } else if ("points" in newProps && !newProps.skipOffset) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < newProps.points.length; i += 2) {
            minX = Math.min(minX, newProps.points[i]);
            maxX = Math.max(maxX, newProps.points[i]);
            minY = Math.min(minY, newProps.points[i + 1]);
            maxY = Math.max(maxY, newProps.points[i + 1]);
          }
          updatedLine.width = maxX - minX;
          updatedLine.height = maxY - minY;
          updatedLine.x = minX;
          updatedLine.y = minY;
          updatedLine.points = newProps.points.map((val, idx) =>
            idx % 2 === 0 ? val - minX : val - minY
          );
        }

        return updatedLine;
      });
    });

    setSelectedObject((prev) => {
      if (!prev || prev.id !== id) {
        return prev;
      }
      // CRITICAL: Determine type - prioritize newProps.type if provided, otherwise determine from arrow flags
      let preservedType = newProps.type;
      if (!preservedType) {
        // If type not in newProps, check if arrow flags changed
        const newHasStartArrow = newProps.hasStartArrow !== undefined ? newProps.hasStartArrow : prev.hasStartArrow;
        const newHasEndArrow = newProps.hasEndArrow !== undefined ? newProps.hasEndArrow : prev.hasEndArrow;

        // Use new arrow flags if provided, otherwise use previous
        // CRITICAL: Don't fall back to pointerAtBeginning/pointerAtEnd - use the explicit flags
        const hasStartArrow = newHasStartArrow === true;
        const hasEndArrow = newHasEndArrow === true;

        if (hasStartArrow && hasEndArrow) {
          preservedType = "DoubleSidedArrow";
        } else if (hasStartArrow || hasEndArrow) {
          preservedType = "Arrow";
        } else {
          preservedType = "Line";
        }
      }
      // If still no type, default to "Line"
      if (!preservedType) {
        preservedType = "Line";
      }
      // CRITICAL: Explicitly set boolean flags to ensure false values are preserved
      const updatedSelectedObject = {
        ...prev,
        ...newProps,
        type: preservedType,
        isFirstTime: false,
        // CRITICAL: Explicitly set boolean flags if they're in newProps
        // This ensures false values override existing true values
        ...(newProps.hasStartArrow !== undefined && { hasStartArrow: Boolean(newProps.hasStartArrow) }),
        ...(newProps.hasEndArrow !== undefined && { hasEndArrow: Boolean(newProps.hasEndArrow) }),
      };
      return updatedSelectedObject;
    });

    // CRITICAL: Only save to history if element is NOT being edited
    // When element is being edited, pendingChanges is already set above, and element_edit_complete will save
    // This groups all property changes into a single history entry when element is unselected
    if (!isEditing) {
      saveToHistory("update_line_properties");
    }
    // If element is being edited, don't save - pendingChanges is already set, and element_edit_complete will save
  };
};

/**
 * Create a function to update arrow properties
 * @param {Object} params - Parameters for updating arrow properties
 * @param {boolean} params.isElementBeingEdited - Whether element is being edited
 * @param {Function} params.setPendingChanges - Setter for pending changes flag
 * @param {Function} params.setSelectedArrow - Setter for selected arrow
 * @param {Function} params.setArrows - Setter for arrows array
 * @param {Function} params.setOffCanvasArrows - Setter for off-canvas arrows array
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to update arrow properties
 */
export const createUpdateArrowProperties = ({
  isElementBeingEditedRef, // Use ref instead of value to avoid stale closures
  setPendingChanges,
  pendingChangesRef, // Ref to update synchronously
  setSelectedArrow,
  setArrows,
  setOffCanvasArrows,
  setSelectedObject,
  saveToHistory,
}) => {
  return (id, newProps) => {
    // Check current value from ref to avoid stale closures
    const isEditing = isElementBeingEditedRef?.current ?? false;

    // Mark that there are pending changes during element editing
    if (isEditing) {
      setPendingChanges(true);
      // CRITICAL: Also update the ref to ensure endElementEditing sees the change
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }
    }

    setSelectedArrow((prev) => {
      if (!prev) return null;
      // Helper function to normalize strokeColor (convert number to hex string if needed)
      const normalizeStrokeColor = (color) => {
        if (!color) return "#000000";
        if (typeof color === "string" && color.startsWith("#")) return color;
        if (typeof color === "string") return color; // Already a string
        if (typeof color === "number") {
          return `#${color.toString(16).padStart(6, "0")}`;
        }
        return "#000000";
      };

      // Normalize strokeColor if it's being updated
      const normalizedProps = { ...newProps };
      if ("strokeColor" in newProps) {
        normalizedProps.strokeColor = normalizeStrokeColor(
          newProps.strokeColor
        );
      }
      if ("stroke" in newProps) {
        normalizedProps.stroke = normalizeStrokeColor(newProps.stroke);
      }

      return { ...prev, ...normalizedProps, id, isFirstTime: false };
    });

    setArrows((prevArrows) =>
      prevArrows.map((arrow) => {
        if (arrow.id !== id) return arrow;


        let updatedArrow = { ...arrow, ...newProps, isFirstTime: false };

        // Only recalculate width/height/x/y from points if points are being updated
        if (
          "points" in newProps &&
          updatedArrow.points &&
          updatedArrow.points.length >= 4 &&
          !newProps.skipOffset
        ) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < updatedArrow.points.length; i += 2) {
            minX = Math.min(minX, updatedArrow.points[i]);
            maxX = Math.max(maxX, updatedArrow.points[i]);
            minY = Math.min(minY, updatedArrow.points[i + 1]);
            maxY = Math.max(maxY, updatedArrow.points[i + 1]);
          }
          updatedArrow.width = maxX - minX;
          updatedArrow.height = maxY - minY;
          updatedArrow.x = minX;
          updatedArrow.y = minY;
          // Offset points to be relative to new x/y
          updatedArrow.points = updatedArrow.points.map((val, idx) =>
            idx % 2 === 0 ? val - minX : val - minY
          );

        } else {
        }

        return updatedArrow;
      })
    );

    // Mirror updates to off-canvas arrows
    setOffCanvasArrows((prevArrows) =>
      prevArrows.map((arrow) => {
        if (arrow.id !== id) return arrow;

        let updatedArrow = { ...arrow, ...newProps, isFirstTime: false };

        if (
          "points" in newProps &&
          updatedArrow.points &&
          updatedArrow.points.length >= 4 &&
          !newProps.skipOffset
        ) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < updatedArrow.points.length; i += 2) {
            minX = Math.min(minX, updatedArrow.points[i]);
            maxX = Math.max(maxX, updatedArrow.points[i]);
            minY = Math.min(minY, updatedArrow.points[i + 1]);
            maxY = Math.max(maxY, updatedArrow.points[i + 1]);
          }
          updatedArrow.width = maxX - minX;
          updatedArrow.height = maxY - minY;
          updatedArrow.x = minX;
          updatedArrow.y = minY;
          updatedArrow.points = updatedArrow.points.map((val, idx) =>
            idx % 2 === 0 ? val - minX : val - minY
          );
        }

        return updatedArrow;
      })
    );

    setSelectedObject((prev) => {
      if (!prev || prev.id !== id) {
        return prev;
      }
      return { ...prev, ...newProps, isFirstTime: false };
    });

    // CRITICAL: Only save to history if element is NOT being edited
    // When element is being edited, pendingChanges is already set above, and element_edit_complete will save
    // This groups all property changes into a single history entry when element is unselected
    if (!isEditing) {
      saveToHistory("update_arrow_properties");
    }
    // If element is being edited, don't save - pendingChanges is already set, and element_edit_complete will save
  };
};

/**
 * Create a function to update double-sided arrow properties
 * @param {Object} params - Parameters for updating double-sided arrow properties
 * @param {boolean} params.isElementBeingEdited - Whether element is being edited
 * @param {Function} params.setPendingChanges - Setter for pending changes flag
 * @param {Function} params.setSelectedDoubleSidedArrow - Setter for selected double-sided arrow
 * @param {Function} params.setDoubleSidedArrows - Setter for double-sided arrows array
 * @param {Function} params.setOffCanvasDoubleSidedArrows - Setter for off-canvas double-sided arrows array
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.saveToHistory - Function to save to history
 * @returns {Function} Function to update double-sided arrow properties
 */
export const createUpdateDoubleSidedArrowProperties = ({
  isElementBeingEditedRef, // Use ref instead of value to avoid stale closures
  setPendingChanges,
  pendingChangesRef, // Ref to update synchronously
  setSelectedDoubleSidedArrow,
  setDoubleSidedArrows,
  setOffCanvasDoubleSidedArrows,
  setSelectedObject,
  saveToHistory,
}) => {
  return (id, newProps) => {
    // Check current value from ref to avoid stale closures
    const isEditing = isElementBeingEditedRef?.current ?? false;

    // Mark that there are pending changes during element editing
    if (isEditing) {
      setPendingChanges(true);
      // CRITICAL: Also update the ref to ensure endElementEditing sees the change
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }
    }

    setSelectedDoubleSidedArrow((prev) => {
      if (!prev) return null;
      return { ...prev, ...newProps, id, isFirstTime: false };
    });

    setDoubleSidedArrows((prevArrows) =>
      prevArrows.map((arrow) => {
        if (arrow.id !== id) return arrow;


        let updatedArrow = { ...arrow, ...newProps, isFirstTime: false };

        // Only recalculate width/height/x/y from points if points are being updated
        if (
          "points" in newProps &&
          updatedArrow.points &&
          updatedArrow.points.length >= 4 &&
          !newProps.skipOffset
        ) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < updatedArrow.points.length; i += 2) {
            minX = Math.min(minX, updatedArrow.points[i]);
            maxX = Math.max(maxX, updatedArrow.points[i]);
            minY = Math.min(minY, updatedArrow.points[i + 1]);
            maxY = Math.max(maxY, updatedArrow.points[i + 1]);
          }
          updatedArrow.width = maxX - minX;
          updatedArrow.height = maxY - minY;
          updatedArrow.x = minX;
          updatedArrow.y = minY;
          // Offset points to be relative to new x/y
          updatedArrow.points = updatedArrow.points.map((val, idx) =>
            idx % 2 === 0 ? val - minX : val - minY
          );

        } else {
        }

        return updatedArrow;
      })
    );

    // Mirror updates to off-canvas double sided arrows
    setOffCanvasDoubleSidedArrows((prevArrows) =>
      prevArrows.map((arrow) => {
        if (arrow.id !== id) return arrow;

        let updatedArrow = { ...arrow, ...newProps, isFirstTime: false };

        if (
          "points" in newProps &&
          updatedArrow.points &&
          updatedArrow.points.length >= 4 &&
          !newProps.skipOffset
        ) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          for (let i = 0; i < updatedArrow.points.length; i += 2) {
            minX = Math.min(minX, updatedArrow.points[i]);
            maxX = Math.max(maxX, updatedArrow.points[i]);
            minY = Math.min(minY, updatedArrow.points[i + 1]);
            maxY = Math.max(maxY, updatedArrow.points[i + 1]);
          }
          updatedArrow.width = maxX - minX;
          updatedArrow.height = maxY - minY;
          updatedArrow.x = minX;
          updatedArrow.y = minY;
          updatedArrow.points = updatedArrow.points.map((val, idx) =>
            idx % 2 === 0 ? val - minX : val - minY
          );
        }

        return updatedArrow;
      })
    );

    setSelectedObject((prev) => {
      if (!prev || prev.id !== id) {
        return prev;
      }
      return { ...prev, ...newProps, isFirstTime: false };
    });

    // CRITICAL: Only save to history if element is NOT being edited
    // When element is being edited, pendingChanges is already set above, and element_edit_complete will save
    // This groups all property changes into a single history entry when element is unselected
    if (!isEditing) {
      saveToHistory("update_double_sided_arrow_properties");
    }
    // If element is being edited, don't save - pendingChanges is already set, and element_edit_complete will save
  };
};
